import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  IconArrowUpOutline,
  IconCloseOutline,
} from '@ssc-ui-icons/icons-react';

import { i18n } from '@/i18n';
import styles from './index.module.less';
import aiExplainIcon from '@/assets/icons/ai.svg';
import aiThinkingIcon from '@/assets/icons/ai-thinking.svg';
import thumbDownIcon from '@/assets/icons/thumb-down.svg';
import thumbDownActiveIcon from '@/assets/icons/thumb-down-active.svg';
import thumbUpIcon from '@/assets/icons/thumb-up.svg';
import thumbUpActiveIcon from '@/assets/icons/thumb-up-active.svg';
import {
  ConsultDialogueStatus,
  ConsultFeedbackStatus,
  feedbackConsultAnswer,
} from '@/api/rule-center/dynamic-rule';

let msgIdCounter = 0;
const consultCancelPromiseKey = '__configCopilotCancelPromise';

export interface ChatMessage {
  role: 'user' | 'ai';
  id: string;
  content: string;
  session_id?: string;
  task_id?: number;
  dialogue_status?: number;
  feedback_status?: number;
}

export interface ConsultResult {
  answer: string;
  session_id: string;
  task_id?: number;
  dialogue_status?: number;
  feedback_status?: number;
  follow_up_questions?: string[];
}

export type ConsultFn = (
  userPrompt: string,
  sessionId: string,
  signal?: AbortSignal
) => Promise<ConsultResult>;

export interface AiChatPanelProps {
  messages: ChatMessage[];
  sessionId: string;
  initialLoading?: boolean;
  title?: string;
  contextLabel?: string;
  contextAnswer?: string;
  userName?: string;
  suggestions?: string[];
  consultFn: ConsultFn;
  onClose: () => void;
  onMessagesChange: (messages: ChatMessage[], sessionId: string) => void;
  onSuggestionsChange?: (suggestions: string[]) => void;
}

const AiChatPanel: React.FC<AiChatPanelProps> = ({
  messages: initialMessages,
  sessionId: initialSessionId,
  initialLoading,
  title,
  contextLabel,
  contextAnswer,
  userName,
  suggestions: initialSuggestions,
  consultFn,
  onClose,
  onMessagesChange,
  onSuggestionsChange,
}) => {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(initialLoading || false);
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [suggestions, setSuggestions] = useState<string[]>(
    initialSuggestions ?? []
  );
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const isComposingRef = useRef(false);
  const scrollStateRef = useRef({
    messageCount: messages.length,
    loading,
    initialized: false,
  });

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    setMessages(initialMessages);
  }, [initialMessages]);

  useEffect(() => {
    setSessionId(initialSessionId);
  }, [initialSessionId]);

  useEffect(() => {
    setLoading(initialLoading || false);
  }, [initialLoading]);

  useEffect(() => {
    setSuggestions(initialSuggestions ?? []);
  }, [initialSuggestions]);

  useEffect(() => {
    const previous = scrollStateRef.current;
    const shouldScroll =
      !previous.initialized ||
      previous.messageCount !== messages.length ||
      previous.loading !== loading;
    scrollStateRef.current = {
      messageCount: messages.length,
      loading,
      initialized: true,
    };
    if (shouldScroll && chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleClose = useCallback(() => {
    abortRef.current?.abort();
    onClose();
  }, [onClose]);

  const canRateMessage = (msg: ChatMessage) =>
    msg.role === 'ai' &&
    !!msg.session_id &&
    !!msg.task_id &&
    (msg.dialogue_status === ConsultDialogueStatus.Success ||
      msg.dialogue_status === ConsultDialogueStatus.Blocked);

  const handleFeedback = useCallback(
    async (msg: ChatMessage, nextStatus: number) => {
      if (!canRateMessage(msg)) return;
      const feedbackStatus =
        msg.feedback_status === nextStatus
          ? ConsultFeedbackStatus.Unrated
          : nextStatus;
      try {
        const res = await feedbackConsultAnswer({
          session_id: msg.session_id,
          task_id: msg.task_id,
          feedback_status: feedbackStatus,
        });
        if (
          (res?.retcode !== undefined && res.retcode !== 0) ||
          (res?.code !== undefined && res.code !== 0)
        ) {
          throw new Error(res?.message || i18n('Request failed'));
        }
        const savedStatus = res?.data?.feedback_status ?? feedbackStatus;
        const updatedMessages = messages.map((item) =>
          item.id === msg.id
            ? {
                ...item,
                feedback_status: savedStatus,
              }
            : item
        );
        setMessages(updatedMessages);
        onMessagesChange(updatedMessages, sessionId);
      } catch (error) {
        console.error(error);
      }
    },
    [messages, onMessagesChange, sessionId]
  );

  const handleStop = useCallback(async () => {
    const abortController = abortRef.current;
    if (!abortController) return;
    abortController.abort();
    abortRef.current = null;
    let cancelSuccess = true;
    const cancelPromise = (abortController.signal as any)?.[
      consultCancelPromiseKey
    ] as Promise<unknown> | undefined;
    if (cancelPromise) {
      try {
        await cancelPromise;
      } catch (error) {
        cancelSuccess = false;
        console.error(error);
      }
    }
    const aiMsg: ChatMessage = {
      id: `msg-${++msgIdCounter}`,
      role: 'ai',
      content: cancelSuccess
        ? i18n('Consultation cancelled.')
        : i18n('Failed to cancel consultation. Please try again later.'),
    };
    const updatedMessages = [...messages, aiMsg];
    setMessages(updatedMessages);
    setLoading(false);
    onMessagesChange(updatedMessages, sessionId);
  }, [messages, onMessagesChange, sessionId]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || loading) return;

      const userMsg: ChatMessage = {
        role: 'user',
        content: trimmed,
        id: `msg-${++msgIdCounter}`,
      };
      const newMessages = [...messages, userMsg];
      setMessages(newMessages);
      setInput('');
      setLoading(true);
      setSuggestions([]);

      abortRef.current?.abort();
      const abortController = new AbortController();
      abortRef.current = abortController;

      try {
        const res = await consultFn(trimmed, sessionId, abortController.signal);
        if (abortController.signal.aborted) return;
        const aiContent = res?.answer || i18n('No Data');
        const newSessionId = res?.session_id || sessionId;
        const aiMsg: ChatMessage = {
          role: 'ai',
          content: aiContent,
          id: `msg-${++msgIdCounter}`,
          session_id: newSessionId,
          task_id: res?.task_id,
          dialogue_status: res?.dialogue_status,
          feedback_status: res?.feedback_status,
        };
        const updatedMessages = [...newMessages, aiMsg];
        setMessages(updatedMessages);
        setSessionId(newSessionId);
        const newSuggestions = res?.follow_up_questions ?? [];
        setSuggestions(newSuggestions);
        onSuggestionsChange?.(newSuggestions);
        onMessagesChange(updatedMessages, newSessionId);
      } catch (error: any) {
        if (error?.name === 'AbortError') return;
        console.error(error);
        const aiMsg: ChatMessage = {
          id: `msg-${++msgIdCounter}`,
          role: 'ai',
          content: i18n(
            'AI assistant is temporarily unavailable, please try again later.'
          ),
        };
        const updatedMessages = [...newMessages, aiMsg];
        setMessages(updatedMessages);
        setSuggestions([]);
        onSuggestionsChange?.([]);
        onMessagesChange(updatedMessages, sessionId);
      } finally {
        if (abortRef.current === abortController) {
          abortRef.current = null;
          setLoading(false);
        }
      }
    },
    [
      loading,
      messages,
      sessionId,
      consultFn,
      onMessagesChange,
      onSuggestionsChange,
    ]
  );

  const handleSend = useCallback(() => {
    sendMessage(input);
  }, [input, sendMessage]);

  const handleSuggestionClick = useCallback(
    (text: string) => {
      sendMessage(text);
    },
    [sendMessage]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const nativeEvent = e.nativeEvent as KeyboardEvent & {
        isComposing?: boolean;
      };
      if (
        nativeEvent.isComposing ||
        isComposingRef.current ||
        e.keyCode === 229
      ) {
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback(() => {
    window.setTimeout(() => {
      isComposingRef.current = false;
    }, 0);
  }, []);

  return (
    <div className={styles.aiChatPanel}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <img className={styles.avatar} src={aiExplainIcon} alt="AI" />
          <span className={styles.title}>
            {title || i18n('WMS AI Assistant')}
          </span>
        </div>
        <button
          type="button"
          className={styles.closeBtn}
          aria-label={i18n('Close')}
          onClick={handleClose}
        >
          <IconCloseOutline aria-hidden="true" />
        </button>
      </div>
      <div className={styles.body}>
        {messages.length === 0 && !loading && !contextLabel ? (
          <div className={styles.welcomePage}>
            <div className={styles.welcomeContent}>
              <img
                className={styles.welcomeIcon}
                src={aiExplainIcon}
                alt="AI"
              />
              <div className={styles.welcomeGreeting}>
                {i18n('Hi')}
                {userName ? ', ' + userName : ''}
              </div>
              <div className={styles.welcomeDesc}>
                {i18n(
                  'I am your AI Assistant, specializing in Dynamic Wave configuration knowledge. How can I assist you today?'
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.chatArea} ref={chatAreaRef}>
            <div className={styles.chatAreaInner}>
              {contextLabel && contextAnswer ? (
                <div className={styles.contextCard}>
                  <div className={styles.contextLabel}>{contextLabel}</div>
                  <div className={styles.contextAnswer}>{contextAnswer}</div>
                </div>
              ) : null}
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`${styles.messageRow} ${
                    msg.role === 'user'
                      ? styles.messageRowUser
                      : styles.messageRowAi
                  }`}
                >
                  <div
                    className={`${styles.chatContent} ${
                      msg.role === 'user'
                        ? styles.chatContentUser
                        : styles.chatContentAi
                    }`}
                  >
                    {msg.content}
                  </div>
                  {canRateMessage(msg) ? (
                    <div className={styles.feedbackActions}>
                      <button
                        type="button"
                        className={`${styles.feedbackBtn} ${
                          msg.feedback_status === ConsultFeedbackStatus.Helpful
                            ? styles.feedbackBtnActive
                            : ''
                        }`}
                        aria-label={i18n('Helpful')}
                        onClick={() =>
                          handleFeedback(msg, ConsultFeedbackStatus.Helpful)
                        }
                      >
                        <img
                          className={styles.feedbackIcon}
                          src={
                            msg.feedback_status ===
                            ConsultFeedbackStatus.Helpful
                              ? thumbUpActiveIcon
                              : thumbUpIcon
                          }
                          alt=""
                          aria-hidden="true"
                        />
                      </button>
                      <button
                        type="button"
                        className={`${styles.feedbackBtn} ${
                          msg.feedback_status ===
                          ConsultFeedbackStatus.Unhelpful
                            ? styles.feedbackBtnActive
                            : ''
                        }`}
                        aria-label={i18n('Not helpful')}
                        onClick={() =>
                          handleFeedback(msg, ConsultFeedbackStatus.Unhelpful)
                        }
                      >
                        <img
                          className={styles.feedbackIcon}
                          src={
                            msg.feedback_status ===
                            ConsultFeedbackStatus.Unhelpful
                              ? thumbDownActiveIcon
                              : thumbDownIcon
                          }
                          alt=""
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
              {loading && (
                <div className={styles.thinkingBubble}>
                  <img
                    className={styles.thinkingIcon}
                    src={aiThinkingIcon}
                    alt=""
                  />
                  <span className={styles.thinkingText}>
                    {i18n('Thinking...')}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        {suggestions.length > 0 && !loading && (
          <div className={styles.suggestions}>
            {suggestions.map((q) => (
              <button
                key={q}
                type="button"
                className={styles.suggestionChip}
                onClick={() => handleSuggestionClick(q)}
              >
                {q}
              </button>
            ))}
          </div>
        )}
        <div className={styles.inputArea}>
          <textarea
            className={styles.input}
            placeholder={i18n('Please enter...')}
            value={input}
            rows={4}
            disabled={loading}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
          />
          <button
            type="button"
            className={`${styles.sendBtn} ${
              loading ? styles.sendBtnLoading : ''
            }`}
            aria-label={loading ? i18n('Stop') : i18n('Send')}
            disabled={!loading && !input.trim()}
            onClick={loading ? handleStop : handleSend}
          >
            {loading ? (
              <span className={styles.stopIndicator} />
            ) : (
              <IconArrowUpOutline />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiChatPanel;
